import {useApolloClient} from '@apollo/client';
import {DepositoryType, useTrackedBalancesQuery} from '@transactions/graphql';
import {parseDate, today} from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {AccountAvatar} from 'src/AccountAvatar';
import {AmountDisplay} from 'src/AmountDisplay';
import {useUnitsById} from 'src/hooks/useUnits';
import {inboxCardSurface} from 'src/InboxCard';
import { formatMoneyWithUnits} from 'src/lib/unitDisplay';
import {cn} from 'src/lib/utils';
import {useModals} from 'src/Modals';
import type {TrackedBalancesQuery} from '@transactions/graphql';
import type {UnitsById} from 'src/lib/unitDisplay';

type OutstandingRow = TrackedBalancesQuery['trackedBalances'][number];

// The inbox stays scannable no matter how many credits accumulate: the card
// shows the largest few and the rest sit behind an inline expander.
const VISIBLE_ROWS = 4;

// Credits are stored value (wallet); anything else tracked is a loan
const isCredit = (row: OutstandingRow): boolean =>
  row.account.depositoryType === DepositoryType.Wallet;

const ageLabel = (row: OutstandingRow): string | null => {
  if (row.lastActivityDate == null) return null;
  const days = Math.max(
    0,
    Math.round(today().diff(parseDate(row.lastActivityDate), 'days').days),
  );
  const verb = isCredit(row) ? 'banked' : row.balance >= 0 ? 'lent' : 'borrowed';
  if (days < 1) return `${verb} today`;
  if (days < 60) return `${verb} ${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 24) return `${verb} ${months} months ago`;
  return `${verb} ${Math.round(months / 12)} years ago`;
};

/**
 * Whole-unit total in an account's currency. Custom units render through
 * their Unit metadata; Intl's currency style throws a RangeError on the
 * non-ISO codes the create-account modal advertises (TENNIS_CLASS), so this
 * falls back the same way Money.toDecimalString does: a bare number with the
 * code appended.
 */
const wholeUnitTotal = (
  hundredths: number,
  currency: string,
  unitsById: UnitsById,
): string => {
  if (unitsById.has(currency)) {
    return formatMoneyWithUnits(
      Money.fromInt(hundredths, currency),
      unitsById,
    );
  }
  const units = hundredths / 100;
  try {
    return units.toLocaleString('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });
  } catch {
    return `${units.toLocaleString('en-US', {maximumFractionDigits: 0})} ${currency}`;
  }
};

const summaryLine = (rows: OutstandingRow[], unitsById: UnitsById): string => {
  const credits = rows.filter(isCredit);
  const loans = rows.filter((row) => !isCredit(row));
  // Summed dollar totals only make sense in a single currency; with mixed
  // currencies fall back to counts (per-row amounts stay fully labeled)
  const mixed = new Set(rows.map((row) => row.account.currency)).size > 1;
  const listCurrency = rows[0]?.account.currency ?? 'USD';
  const total = (list: OutstandingRow[]): string =>
    mixed
      ? ''
      : ` · ${wholeUnitTotal(
          list.reduce((sum, row) => sum + Math.abs(row.balance), 0),
          listCurrency,
          unitsById,
        )}`;
  return [
    credits.length > 0 &&
      `${credits.length} credit${credits.length === 1 ? '' : 's'}${total(credits)}`,
    loans.length > 0 &&
      `${loans.length} loan${loans.length === 1 ? '' : 's'}${total(loans)}`,
  ]
    .filter(Boolean)
    .join('  ·  ');
};

/**
 * Standing card of open tracked accounts (banked credits, outstanding loans)
 * on the inbox. Rows resolve in place — record use, repayment, write off —
 * and disappear the moment their derived balance settles to zero.
 */
export const InboxOutstandingCard = () => {
  const modals = useModals();
  const unitsById = useUnitsById();
  const client = useApolloClient();
  const [expanded, setExpanded] = React.useState(false);

  const {data, error, refetch} = useTrackedBalancesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.trackedBalances ?? [];

  // A failed query must not silently hide open credits/loans
  if (data === undefined && error) {
    return (
      <div className={cn(inboxCardSurface, 'px-4 py-3')}>
        <span className="text-muted-foreground text-xs">
          Couldn&apos;t load outstanding balances.{' '}
        </span>
        <button
          type="button"
          className="text-foreground cursor-pointer text-xs underline"
          onClick={() => void refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  const visible = expanded ? rows : rows.slice(0, VISIBLE_ROWS);
  const hiddenCount = rows.length - visible.length;
  const totalOutstanding = rows.reduce((sum, row) => sum + Math.abs(row.balance), 0);

  const handleDone = () => {
    void refetch();
    // AllAccounts caches the derived balance field; keep it in sync
    void client.refetchQueries({include: ['AllAccounts']});
  };

  const openResolution = (row: OutstandingRow) => {
    if (isCredit(row)) {
      modals.recordUseModal.openModal({
        accountId: row.account._id,
        accountName: row.account.name,
        currency: row.account.currency,
        balance: row.balance,
        latestAnchorDate: row.latestAnchorDate ?? null,
        onDone: handleDone,
      });
    } else {
      modals.recordLoanRepaymentModal.openModal({
        accountId: row.account._id,
        accountName: row.account.name,
        currency: row.account.currency,
        balance: row.balance,
        latestAnchorDate: row.latestAnchorDate ?? null,
        onDone: handleDone,
      });
    }
  };

  return (
    <div className={cn(inboxCardSurface, 'px-4 py-1')}>
      <div className="flex items-baseline justify-between gap-2 py-2.5">
        <span className="text-foreground text-sm font-semibold">Outstanding</span>
        <span className="text-muted-foreground text-xs">
          {summaryLine(rows, unitsById)}
        </span>
      </div>
      {visible.map((row) => (
        <Link
          key={row.account._id}
          to={`/accounts/${row.account._id}`}
          className="border-border hover:bg-muted-foreground/10 -mx-4 grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-0.5 border-t px-4 py-2.5"
        >
          <AccountAvatar account={row.account} className="row-span-2 size-5" />
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-foreground truncate text-sm font-medium">
              {row.account.name}
            </span>
            <span className="border-border text-muted-foreground rounded-full border px-2 py-0.5 text-[0.7rem]">
              {isCredit(row) ? 'credit' : 'loan'}
            </span>
          </div>
          <div className="text-right text-sm font-semibold tabular-nums">
            <AmountDisplay
              variant="plain"
              amount={Money.fromInt(Math.abs(row.balance), row.account.currency)}
            />
          </div>
          <div className="text-muted-foreground text-xs">{ageLabel(row)}</div>
          <div className="flex justify-end text-xs">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openResolution(row);
              }}
            >
              {isCredit(row) ? 'Record use ›' : 'Record repayment ›'}
            </button>
          </div>
        </Link>
      ))}
      {(hiddenCount > 0 || expanded) && (
        <button
          type="button"
          className="border-border text-muted-foreground hover:text-foreground w-full cursor-pointer border-t py-2.5 text-center text-xs"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? 'Show fewer'
            : new Set(rows.map((row) => row.account.currency)).size > 1
              ? `Show all ${rows.length}`
              : `Show all ${rows.length} · ${wholeUnitTotal(
                  totalOutstanding,
                  rows[0]?.account.currency ?? 'USD',
                  unitsById,
                )} outstanding`}
        </button>
      )}
    </div>
  );
};
